import { isEmptyString } from '@/utils/string';

export const DeliverySettings = {
  Normal: 'normal',
  SendToOne: 'send_to_one',
  Off: 'off',
} as const;
export type DeliverySettings = (typeof DeliverySettings)[keyof typeof DeliverySettings];

/** Per-identifier enablement state (mirrors the Ember `IdentifierState` enum). */
export const IdentifierState = {
  Disabled: 0,
  Enabled: 1,
  EnabledImmutable: 2,
} as const;
export type IdentifierState = (typeof IdentifierState)[keyof typeof IdentifierState];

/** The identifiers a workspace can use (mirrors the Ember `IdentifierKey` enum). */
export const IdentifierKey = {
  CioId: 'cio_id',
  Id: 'id',
  Email: 'email',
  Phone: 'phone',
} as const;
export type IdentifierKey = (typeof IdentifierKey)[keyof typeof IdentifierKey];

/** A workspace identifies people either by email only, or by multiple identifiers. */
export const IdentifierType = {
  Email: 'email',
  MultiIdentifier: 'multi',
} as const;
export type IdentifierType = (typeof IdentifierType)[keyof typeof IdentifierType];

/** Target configurations the self-serve identifier migration can move a workspace to. */
export const SelfServeMigrateTo = {
  IdOnly: 'id_only',
  IdMutable: 'id_mutable',
  MultiId: 'multi_id',
} as const;
export type SelfServeMigrateTo = (typeof SelfServeMigrateTo)[keyof typeof SelfServeMigrateTo];

/**
 * Workspace enforcement mode for the `cio_open_tracking_consent` attribute. Off applies no consent
 * gate; opt-in tracks only explicitly consented people; opt-out tracks everyone except people who
 * explicitly opted out. Mirrors the Ember `OpenTrackingConsentMode` enum.
 */
export const OpenTrackingConsentMode = {
  Off: 'off',
  OptIn: 'opt_in',
  OptOut: 'opt_out',
} as const;
export type OpenTrackingConsentMode = (typeof OpenTrackingConsentMode)[keyof typeof OpenTrackingConsentMode];

/** How duplicate profiles are merged when enabling email as an identifier. */
export const AutoMergeOption = {
  Manual: 'manual',
  Newest: 'keep_newest',
  Oldest: 'keep_oldest',
} as const;
export type AutoMergeOption = (typeof AutoMergeOption)[keyof typeof AutoMergeOption];

/** Identifier-state values (`IdentifierState`) keyed by identifier. */
export interface WorkspaceIdentifiers {
  cio_id: number;
  id: number;
  email: number;
  phone?: number;
}

/** Reference-rule settings (which identifiers people can be referenced by). */
export interface IdentifierSettings {
  identify_by_id: boolean;
}

/**
 * Identifier configuration read off a loaded environment, plus the migration in
 * progress. Mirrors the fields the Ember environment model derives its
 * identifier getters from.
 */
export interface EnvironmentIdentifierConfig {
  identifier_type: string;
  identifiers?: WorkspaceIdentifiers;
  identifier_settings: IdentifierSettings;
  identifiers_migration_in_progress: string;
}

/**
 * The derived identifier flags the Ember environment model exposes as computed
 * getters. Centralised here so the React identifiers UI reads the same booleans
 * the Ember template did rather than re-deriving them inline.
 */
export interface EnvironmentIdentifierFlags {
  isMultiIdentifier: boolean;
  canIdentifyByEmail: boolean;
  canIdentifyByPhone: boolean;
  canIdentifyById: boolean;
  isIdentifyingById: boolean;
  isMigrating: boolean;
  migratingToImmutableId: boolean;
  migratingToMutableId: boolean;
  migratingToMultiId: boolean;
}

/** An identifier is enabled when it's present and not explicitly disabled. */
export function isIdentifierEnabled(state: number | undefined): boolean {
  return state != null && state !== IdentifierState.Disabled;
}

/**
 * Classify a workspace's identifier configuration for analytics — mirrors the
 * Ember `currentIdentifier` analytics transform.
 */
export function currentIdentifierConfigLabel(identifiers: WorkspaceIdentifiers): string {
  if (identifiers.cio_id === IdentifierState.EnabledImmutable && identifiers.email === IdentifierState.Enabled) {
    return 'multi-id';
  }
  if (identifiers.cio_id === IdentifierState.Disabled && identifiers.email === IdentifierState.Enabled) {
    return 'email-based';
  }
  return 'id-based (Classic)';
}

/**
 * Derive the environment's identifier flags from its raw config — the React
 * equivalent of the Ember environment model's `isMultiIdentifier`,
 * `canIdentifyBy*`, and `migratingTo*` computed getters.
 */
export function deriveEnvironmentIdentifierFlags(config: EnvironmentIdentifierConfig): EnvironmentIdentifierFlags {
  const identifiers = config.identifiers;
  const migration = config.identifiers_migration_in_progress;
  return {
    isMultiIdentifier: config.identifier_type === IdentifierType.MultiIdentifier,
    canIdentifyByEmail: isIdentifierEnabled(identifiers?.email),
    canIdentifyByPhone: isIdentifierEnabled(identifiers?.phone),
    canIdentifyById: isIdentifierEnabled(identifiers?.id),
    isIdentifyingById: config.identifier_settings.identify_by_id,
    // Match Ember's `@notEmpty`: a missing/null/empty value means no migration is running. A bare
    // `!== ''` would treat an omitted `identifiers_migration_in_progress` as an active migration
    // and wrongly lock the identifiers panel.
    isMigrating: !isEmptyString(migration),
    migratingToImmutableId: migration === SelfServeMigrateTo.IdOnly,
    migratingToMutableId: migration === SelfServeMigrateTo.IdMutable,
    migratingToMultiId: migration === SelfServeMigrateTo.MultiId,
  };
}

export interface MergedWorkspace {
  id: number;
  account_id: number;
  cio_workspace_id: number;
  cdp_workspace_id: number;
  cio_initialized: boolean;
  cdp_initialized: boolean;
  name: string;
  color: string;
  delivery_settings: DeliverySettings;
  delivery_email: string;
  email_open_tracking: boolean;
  identifier_type: string;
  identifiers?: WorkspaceIdentifiers;
  user_ids: string[];
}

// The object type shape is owned by the backend; use the generated model.
export type { ObjectType as WorkspaceObjectType } from '@customerio/hydra-ui-api';

export interface SampleAccountsApi {
  accounts: SampleAccountApi[];
}

export interface SampleAccountApi {
  environment_ids: number[];
  id: number;
}
